import { type CSSProperties, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** 기준 요소 또는 화면의 한 점 (오른쪽 클릭 메뉴) */
export type Anchor = HTMLElement | { x: number; y: number };

type Placement = "bottom-start" | "bottom-end" | "top-start" | "right-start";

const GAP = 6;
const MARGIN = 8;

function anchorRect(a: Anchor): DOMRect {
  return a instanceof HTMLElement ? a.getBoundingClientRect() : new DOMRect(a.x, a.y, 0, 0);
}

/** 화면 안에 들어오도록 자리를 잡는다: 아래 공간이 모자라면 위로, 좌우는 화면 끝에 맞춘다 */
function place(a: Anchor, el: HTMLElement, placement: Placement): CSSProperties {
  const r = anchorRect(a);
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left: number;
  let top: number;
  if (placement === "right-start") {
    left = r.right + GAP;
    if (left + w > vw - MARGIN) left = r.left - GAP - w;
    top = r.top;
  } else {
    left = placement.endsWith("end") ? r.right - w : r.left;
    const below = r.bottom + GAP;
    const above = r.top - GAP - h;
    const wantAbove = placement.startsWith("top");
    top = wantAbove ? (above >= MARGIN ? above : below) : below + h <= vh - MARGIN || above < MARGIN ? below : above;
  }
  left = Math.min(Math.max(MARGIN, left), vw - MARGIN - w);
  top = Math.min(Math.max(MARGIN, top), vh - MARGIN - h);
  return { top, left };
}

interface PopoverProps {
  anchor: Anchor | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: Placement;
  className?: string;
  role?: string;
  label?: string;
  id?: string;
  /** 마우스로 띄운 미리보기: 기준 요소와 팝오버 밖으로 나가면 닫는다 */
  onPointerLeave?: () => void;
  onPointerEnter?: () => void;
  /** 처음 열 때 안쪽으로 포커스를 옮기지 않는다 (미리보기·툴팁) */
  passive?: boolean;
}

/**
 * 화면 위에 뜨는 상자 (메뉴, 미리보기, 고르기). body에 붙여 스크롤 영역에 잘리지 않는다.
 * 바깥을 누르거나 Esc를 누르면 닫히고, 뒤가 스크롤되면 기준 요소를 따라간다.
 */
export function Popover({ anchor, open, onClose, children, placement = "bottom-start", className = "", role, label, id, onPointerEnter, onPointerLeave, passive }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    if (!open || !anchor || !ref.current) return;
    const el = ref.current;
    const update = () => setStyle(place(anchor, el, placement));
    update();
    // 안의 내용이 바뀌어 크기가 달라지거나, 뒤가 스크롤되어 기준 요소가 움직이면 따라간다
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const onScroll = (e: Event) => !el.contains(e.target as Node) && update();
    window.addEventListener("scroll", onScroll, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, anchor, placement]);

  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) => !!t && (ref.current?.contains(t as Node) || (anchor instanceof HTMLElement && anchor.contains(t as Node)));
    const onDown = (e: PointerEvent) => !inside(e.target) && onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
      if (anchor instanceof HTMLElement) anchor.focus({ preventScroll: true });
    };
    // 화면의 한 점에 뜬 메뉴(오른쪽 클릭)는 스크롤하면 닫는다. 요소에 붙은 것은 따라간다
    const onScroll = (e: Event) => !(anchor instanceof HTMLElement) && !ref.current?.contains(e.target as Node) && onClose();
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [open, anchor, onClose]);

  useEffect(() => {
    if (!open || passive) return;
    const el = ref.current;
    requestAnimationFrame(() => {
      if (!el || el.contains(document.activeElement)) return;
      el.querySelector<HTMLElement>('[autofocus], [role^="menuitem"]:not([disabled]), [role="option"], input, button')?.focus({ preventScroll: true });
    });
  }, [open, passive]);

  if (!open || !anchor) return null;
  return createPortal(
    <div ref={ref} id={id} className={`popover ${className}`} role={role} aria-label={label} style={style} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
      {children}
    </div>,
    document.body,
  );
}

/** 마우스를 잠깐 올리면 열고, 떠나면 조금 뒤에 닫는다 (미리보기·툴팁용). 팝오버 위로 옮겨 가도 열려 있다 */
export function useHoverIntent(delay = 450) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    [],
  );
  const enter = (el: HTMLElement) => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setAnchor(el), delay);
  };
  const leave = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setAnchor(null), 150);
  };
  const keep = () => clearTimeout(closeTimer.current);
  const close = () => {
    clearTimeout(openTimer.current);
    setAnchor(null);
  };
  return { anchor, enter, leave, keep, close, openNow: (el: HTMLElement) => setAnchor(el) };
}

/* ───────────── 메뉴 ───────────── */

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  /** 단축키 안내 */
  hint?: string;
  disabled?: boolean;
  /** 위에 구분선 */
  separated?: boolean;
}

/** role=menu 안에서 화살표·Home·End로 항목을 옮겨 다닌다 */
export function useMenuKeys(open: boolean, ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const root = ref.current?.matches('[role="menu"]') ? ref.current : ref.current?.querySelector<HTMLElement>('[role="menu"]');
    if (!root) return;
    const items = () => [...root.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])')];
    const trigger = document.activeElement instanceof HTMLElement && !root.contains(document.activeElement) ? document.activeElement : null;
    const frame = requestAnimationFrame(() => {
      if (!root.contains(document.activeElement)) items()[0]?.focus({ preventScroll: true });
    });
    const onKey = (e: KeyboardEvent) => {
      const list = items();
      if (!list.length) return;
      const i = list.indexOf(document.activeElement as HTMLElement);
      let next = -1;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") next = (i + 1) % list.length;
      else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = (i - 1 + list.length) % list.length;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = list.length - 1;
      else if (e.key === "Tab") return trigger?.focus();
      if (next < 0) return;
      e.preventDefault();
      list[next]!.focus();
    };
    root.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener("keydown", onKey);
      if (trigger && (root.contains(document.activeElement) || document.activeElement === document.body)) trigger.focus({ preventScroll: true });
    };
  }, [open, ref]);
}

export function MenuItems({ items, onClose }: { items: MenuItem[]; onClose: () => void }) {
  return (
    <>
      {items.map((it) => (
        <div key={it.label} className="menu-row">
          {it.separated && <hr className="menu-sep" />}
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={it.danger ? "btn-danger" : undefined}
            disabled={it.disabled}
            onClick={() => {
              onClose();
              it.onSelect();
            }}
          >
            <span>{it.label}</span>
            {it.hint && <kbd>{it.hint}</kbd>}
          </button>
        </div>
      ))}
    </>
  );
}

/** 버튼이나 화면의 한 점에 뜨는 동작 메뉴 */
export function PopoverMenu({ anchor, open, onClose, items, label, placement = "bottom-end" }: { anchor: Anchor | null; open: boolean; onClose: () => void; items: MenuItem[]; label: string; placement?: Placement }) {
  const ref = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  useMenuKeys(open, wrap);
  return (
    <Popover anchor={anchor} open={open} onClose={onClose} placement={placement} className="menu popover-menu" role="presentation">
      <div ref={wrap}>
        <div ref={ref} role="menu" aria-label={label}>
          <MenuItems items={items} onClose={onClose} />
        </div>
      </div>
    </Popover>
  );
}
